/**
 * Cart drawer extras.
 *
 * The drawer is re-rendered from Liquid on every cart change, so each render
 * creates new elements. <free-shipping-bar> remembers the last progress value
 * on window and animates the new fill from there; <cart-upsell> adds a variant
 * and hands the returned sections to <cart-drawer>.renderContents().
 */
if (!customElements.get('free-shipping-bar')) {
  customElements.define(
    'free-shipping-bar',
    class FreeShippingBar extends HTMLElement {
      connectedCallback() {
        const fill = this.querySelector('.fsb__fill');
        const target = parseFloat(this.dataset.progress) || 0;
        const previous = window.__fsbProgress;
        window.__fsbProgress = target;
        if (!fill || previous === undefined || previous === target) return;

        // Start from the previous width, then let the CSS transition run to the new one.
        fill.style.transition = 'none';
        fill.style.width = `${previous}%`;
        fill.getBoundingClientRect();
        fill.style.transition = '';
        requestAnimationFrame(() => {
          fill.style.width = `${target}%`;
        });

        if (target >= 100 && previous < 100) this.classList.add('fsb--celebrate');
      }
    }
  );

  // Defining the element upgrades any bar already on the page. If there was none
  // (empty cart), the first add should still animate, so start from 0.
  if (window.__fsbProgress === undefined) window.__fsbProgress = 0;
}

if (!customElements.get('cart-upsell')) {
  customElements.define(
    'cart-upsell',
    class CartUpsell extends HTMLElement {
      connectedCallback() {
        this.error = this.querySelector('.cart-upsell__error');
        this.addEventListener('click', (event) => {
          const button = event.target.closest('.cart-upsell__add');
          if (button) this.add(button);
        });
      }

      add(button) {
        if (button.getAttribute('aria-disabled') === 'true') return;
        const cartDrawer = document.querySelector('cart-drawer');
        const variantId = Number(button.dataset.variantId);

        button.setAttribute('aria-disabled', 'true');
        button.classList.add('is-loading');
        if (this.error) this.error.hidden = true;

        const sections = cartDrawer ? cartDrawer.getSectionsToRender().map((section) => section.id) : [];
        const body = JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message || window.cartStrings?.error);
              return;
            }
            if (typeof publish === 'function') {
              // source 'cart-items': the drawer's <cart-drawer-items> skips its own refetch for this
              // source (cart.js), which would otherwise re-render after us and drop the focus trap.
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', productVariantId: variantId, cartData: response });
            }
            if (cartDrawer) {
              cartDrawer.renderContents({ ...response, id: variantId });
              // The re-render drops focus; keep keyboard users inside the drawer.
              setTimeout(() => {
                const inner = cartDrawer.querySelector('.drawer__inner');
                if (inner && typeof trapFocus === 'function') trapFocus(cartDrawer.querySelector('#CartDrawer'), inner);
                const liveRegion = document.getElementById('CartDrawer-LiveRegionText');
                if (liveRegion && button.dataset.addedText) liveRegion.textContent = button.dataset.addedText;
              }, 50);
            } else {
              window.location.reload();
            }
          })
          .catch(() => this.showError(window.cartStrings?.error))
          .finally(() => {
            button.removeAttribute('aria-disabled');
            button.classList.remove('is-loading');
          });
      }

      showError(message) {
        if (!this.error || !message) return;
        this.error.textContent = message;
        this.error.hidden = false;
      }
    }
  );
}
